/**
 * Fetching the deliverable model and turning it into something drawable.
 *
 * The same shape as `usePointCloud`, and deliberately a separate hook rather
 * than a parameter on it: the two draw different things, refuse different
 * things, and have different size budgets. What they do share - authenticated
 * delivery, one in-flight request, a generation guard so a late reply cannot
 * overwrite a newer one - is repeated because it is four lines, not abstracted
 * behind a wrapper that would obscure both.
 *
 * **It loads `model_glb` and nothing else.** The full-resolution mesh is a
 * different artifact of a different order of magnitude; substituting it would
 * mean a browser downloading hundreds of megabytes to draw what a twelve
 * megabyte file already says.
 *
 * **A reopen inside one session does not parse again.** The bytes were already
 * free on a second visit - the artifact route revalidates on its sha256 and
 * answers 304 with no body - but the parse was not, and parsing a 500k-face
 * model is the dominant CPU cost of opening this screen. `meshCache` holds the
 * parsed result, keyed by who is looking as well as by what they are looking
 * at; see that file for why both halves of the identity guard are there.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { getArtifactContent } from "@/api/client";
import { ApiError } from "@/api/errors";
import { parseGlb, MeshParseError, type Mesh } from "./glb";
import { cachedMesh, rememberMesh } from "./meshCache";

/**
 * The largest model this screen will download and draw.
 *
 * The delivered GLB is decimated to 500k faces with vertex colour and measures
 * around 13 MB, so 96 MB is several times the pipeline's own ceiling and still
 * well inside what a browser will hold. Past it the honest answer is the
 * download button, not a tab that stops responding while it parses.
 */
export const MAX_VIEWABLE_MODEL_BYTES = 96 * 1024 * 1024;

export type ModelMeshState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; mesh: Mesh }
  | { status: "too-large"; byteSize: number }
  | { status: "empty" }
  | { status: "failed"; message: string };

function messageFor(error: unknown): string {
  if (error instanceof MeshParseError) return "Model dosyası okunamadı.";
  if (error instanceof ApiError) {
    switch (error.kind) {
      case "not_found":
        // Also what somebody else's artifact looks like, by design - the API
        // refuses both the same way rather than confirming one exists.
        return "Bu model artık mevcut değil.";
      case "timeout":
        return "Model zamanında indirilemedi.";
      case "network":
        return "Sunucuya ulaşılamadı.";
      case "unavailable":
      case "server":
        return "Sunucu bu modeli şu anda veremedi.";
      default:
        return "Model yüklenemedi.";
    }
  }
  return "Model yüklenemedi.";
}

export function useModelMesh(
  jobId: string | null,
  artifactId: string | null,
  byteSize: number | null,
  /**
   * Who is looking. Part of the cache key, so one browser profile cannot serve
   * one account's geometry to the next. Optional, and `null` disables caching
   * entirely rather than falling back to a shared bucket.
   */
  userId: string | null = null,
): { state: ModelMeshState; retry: () => void } {
  const [state, setState] = useState<ModelMeshState>({ status: "idle" });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!jobId || !artifactId) {
      setState({ status: "idle" });
      return;
    }
    if (byteSize !== null && byteSize <= 0) {
      setState({ status: "empty" });
      return;
    }
    if (byteSize !== null && byteSize > MAX_VIEWABLE_MODEL_BYTES) {
      setState({ status: "too-large", byteSize });
      return;
    }

    // Already parsed, this session, by this person. No request, no parse, and
    // no `loading` frame - a screen that flashes a spinner for a model it
    // already holds is the same wait the cache exists to remove.
    const remembered = cachedMesh(userId, jobId, artifactId, byteSize);
    if (remembered) {
      generation.current += 1;
      setState({ status: "ready", mesh: remembered });
      return;
    }

    const controller = new AbortController();
    const current = ++generation.current;
    setState({ status: "loading" });

    getArtifactContent(jobId, artifactId, { signal: controller.signal })
      .then((buffer) => {
        if (current !== generation.current || controller.signal.aborted) return;
        // Parsed inside the promise so `buffer` goes out of scope here and
        // only the typed arrays are retained - the difference between holding
        // one copy of a 13 MB model and two.
        const mesh = parseGlb(buffer);
        if (mesh.faceCount === 0) {
          setState({ status: "empty" });
          return;
        }
        // Only a real mesh is remembered. Caching an empty or failed result
        // would make a transient server problem stick for the whole session.
        rememberMesh(userId, jobId, artifactId, byteSize, mesh);
        setState({ status: "ready", mesh });
      })
      .catch((error: unknown) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState({ status: "failed", message: messageFor(error) });
      });

    return () => controller.abort();
  }, [jobId, artifactId, byteSize, userId, attempt]);

  return { state, retry };
}
